import type { CollectionResponse, UnifiedCollection } from './types';

export function isArtifactsCollection(collection: CollectionResponse): boolean {
  return collection.kind === 'artifacts';
}

export function isInputCollection(collection: CollectionResponse): boolean {
  return collection.kind !== 'artifacts';
}

export function splitCollectionsByKind(collections: CollectionResponse[]) {
  const inputCollections = collections.filter(isInputCollection);
  const outputCollections = collections.filter(isArtifactsCollection);
  return { inputCollections, outputCollections };
}

type EvidenceEligibleCollection =
  | Pick<CollectionResponse, 'kind'>
  | Pick<UnifiedCollection, 'kind'>;

/** Input collections and documents eligible for Evidence source control. */
export function isEvidenceEligibleCollection(
  collection: EvidenceEligibleCollection
): boolean {
  return collection.kind !== 'artifacts';
}

export type CollectionAllowlistAlias = {
  id: string;
  legacySourceListId?: string | null;
};

/**
 * Expand a team enforcement allowlist so both the unified collection UUID and
 * the pre-migration source-list ObjectId match. Used by the picker only —
 * do not feed the expanded hex IDs into sanitizeSourceConfig.
 */
export function expandAllowlistWithLegacyAliases(
  allowlist: readonly string[] | null | undefined,
  collections: readonly CollectionAllowlistAlias[]
): string[] | null {
  if (!allowlist || allowlist.length === 0) {
    return allowlist ? [...allowlist] : null;
  }
  const expanded = new Set(allowlist);
  for (const collection of collections) {
    const legacyId = collection.legacySourceListId;
    if (!legacyId) continue;
    if (expanded.has(collection.id) || expanded.has(legacyId)) {
      expanded.add(collection.id);
      expanded.add(legacyId);
    }
  }
  return [...expanded];
}

/**
 * Rewrite stored team web allowlist IDs onto unified collection UUIDs when a
 * migrated collection is known. Unmigrated 24-hex IDs are left as-is so
 * sanitizeSourceConfig can still pin live source lists. Never injects a hex
 * alias next to a UUID (that would pollute `source_list_ids`).
 */
export function resolveAllowlistToUnifiedIds(
  allowlist: readonly string[] | null | undefined,
  collections: readonly CollectionAllowlistAlias[]
): string[] | null {
  if (!allowlist || allowlist.length === 0) {
    return allowlist ? [...allowlist] : null;
  }
  const uuidByLegacy = new Map<string, string>();
  for (const collection of collections) {
    if (collection.legacySourceListId) {
      uuidByLegacy.set(collection.legacySourceListId, collection.id);
    }
  }
  const resolved: string[] = [];
  const seen = new Set<string>();
  for (const id of allowlist) {
    const mapped = uuidByLegacy.get(id) ?? id;
    if (seen.has(mapped)) continue;
    seen.add(mapped);
    resolved.push(mapped);
  }
  return resolved;
}

export function isIdInAllowlist(
  id: string,
  allowlist: readonly string[] | null | undefined,
  legacySourceListId?: string | null
): boolean {
  if (!allowlist || allowlist.length === 0) return true;
  if (allowlist.includes(id)) return true;
  return Boolean(legacySourceListId && allowlist.includes(legacySourceListId));
}
